<!-- eslint-disable vue/no-mutating-props -->
<template>
  <v-dialog
    v-model="visible"
    :max-width="800"
    scrim="transparent"
  >
    <v-card
      v-if="enrollment"
      border
      flat
      tile
    >
      <v-card-title>
        <span>{{ enrollmentAriaLabel }}</span>
      </v-card-title>

      <v-divider class="mb-6" />

      <v-card-text
        v-if="props.providers.length > 0"
        class="py-0"
      >
        <div class="mb-6">
          <label
            class="c-black fs-16 fw-500 mb-2"
            for="provider-select"
          >
            {{ $bct('What location will they enroll in?') }}
          </label>
          <!-- eslint-disable-next-line prefer-standard-components/no-vuetify-inputs -- TODO migrate to LabeledSelect -->
          <v-select
            v-model="provider"
            id="provider-select"
            :item-title="(provider) => provider.name"
            :items="props.providers"
            :readonly="!store.state.pages.Interested.features.enable_provider_transfer"
            item-value="id"
            variant="filled"
            hide-details
            return-object
          />
        </div>

        <div
          v-if="store.state.pages.Interested.features.enable_program_assignment"
          class="mb-6"
        >
          <p class="c-black fs-16 fw-500 mb-2">
            {{ $bct('What program or class will they enroll in?') }}
          </p>
          <!-- eslint-disable-next-line prefer-standard-components/no-vuetify-inputs -- TODO migrate to LabeledSelect -->
          <v-select
            v-model="program"
            :aria-label="$bct('Program')"
            :disabled="!provider"
            :item-title="(program) => $bct(program.name)"
            :items="programs"
            item-value="id"
            variant="filled"
            hide-details
            return-object
          />
        </div>

        <div
          v-if="subsidyProgram?.enable_enrollment_funding_sources"
          data-testid="enrollment-funding-sources-section"
        >
          <p class="c-black fs-16 fw-500 mb-2">
            {{ $bct('Funding source(s)') }}
          </p>
          <LabeledAutocomplete
            v-model="enrollment.funding_source_ids"
            :items="fundingSources"
            item-title="name"
            item-value="id"
            style="margin-bottom: 20px; padding: 0"
            chips
            clearable
            deletable-chips
            filled
            multiple
          />
        </div>

        <div
          v-if="props.subsidyProgram?.enrollment_start_date_required"
          class="mb-6"
        >
          <template v-if="!props.subsidyProgram.enrollment_start_date_title">
            <p class="c-black fs-16 fw-500 mb-2">
              {{ $bct('What day do they start?') }}
            </p>
          </template>
          <template v-else>
            <p class="c-black fs-16 fw-500 mb-1">
              {{ $bct(props.subsidyProgram.enrollment_start_date_title) }}
            </p>
            <template v-if="props.subsidyProgram.enrollment_start_date_subtitle">
              <p class="c-light-black fs-16 fw-500 mb-2 pb-3">
                {{ $bct(props.subsidyProgram.enrollment_start_date_subtitle) }}
              </p>
            </template>
          </template>

          <DatePicker
            v-model="startDate"
            :landscape="$vuetify.display.mdAndUp"
            :min="minStartDate"
            class="b-1 dense bc-primary"
            data-testid="enrollment-startdate-picker"
            full-width
          />
        </div>

        <div
          v-if="store.state.pages.Students?.features?.enable_tuition_amount"
          class="mb-6"
        >
          <v-row>
            <LabeledTextfield
              v-model="rate"
              cols="6"
              message="Payment amount"
              prefix="$"
            />
            <LabeledSelect
              v-model="period"
              :items="['Weekly', 'Bi-weekly', 'Monthly']"
              :multiple="false"
              cols="6"
              message="Payment frequency"
            />
          </v-row>
        </div>
      </v-card-text>

      <v-divider class="mb-3" />

      <v-card-actions>
        <v-btn
          @click="close"
          :ripple="false"
          size="x-large"
          variant="text"
        >
          {{ $bct('Cancel') }}
        </v-btn>
        <v-spacer />
        <v-btn
          @click="submit"
          :aria-label="enrollmentAriaLabel"
          :disabled="!provider"
          :loading="processing"
          :ripple="false"
          color="primary"
          size="x-large"
        >
          {{ $bct(terms.enroll) }}
        </v-btn>
      </v-card-actions>
    </v-card>

    <template v-if="enrollment && props.subsidyProgram?.enrolled_form_schema_id">
      <FormDialog
        @save="enroll($event)"
        ref="enrolledFormDialog"
        :enrollment-id="enrollment.id"
        :group-id="enrollment.group_id"
        :provider-id="enrollment.provider_id"
        :schema-id="props.subsidyProgram.enrolled_form_schema_id"
        :subsidy-id="enrollment.subsidy_id"
      />
    </template>
  </v-dialog>
</template>

<script setup>
import { useDate } from 'vuetify';
import { useStore } from 'vuex';

import DatePicker from '@/shared/components/DatePicker.vue';
import FormDialog from '@/shared/components/form/FormDialog.vue';
import LabeledAutocomplete from '@/shared/components/form/LabeledAutocomplete.vue';
import LabeledSelect from '@/shared/components/form/LabeledSelect.vue';
import LabeledTextfield from '@/shared/components/form/LabeledTextfield.vue';
import useApi from '@/shared/composables/useApi';
import useEventBus from '@/shared/composables/useEventBus';
import useTerms from '@/shared/composables/useTerms';

const $bct = inject('translator');
const props = defineProps({
  providers: {
    type: Array,
    default: () => [],
  },
  subsidyProgram: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(['enrolled']);

const { api: Api } = useApi();
const date = useDate();
const eventBus = useEventBus();
const store = useStore();
const { terms } = useTerms();

const enrollment = ref(null);
const enrolledFormDialog = ref(null);
const fundingSources = ref([]);
const visible = ref(false);
const period = ref(null);
const processing = ref(false);
const program = ref(null);
const programs = ref([]);
const provider = ref(null);
const rate = ref(null);
const startDate = ref(new Date());
const today = ref(new Date());

const enrollmentAriaLabel = computed(() => {
  const translatedTerm = $bct(terms.value.enroll);
  const childName = enrollment.value.meta.child.name;
  return `${translatedTerm} ${childName}`;
});

const backdatingAllowed = computed(() => {
  return props.subsidyProgram?.enable_enrollment_start_date_backdating_by_providers;
});

function close() {
  visible.value = false;
}

async function enroll(form) {
  processing.value = true;

  const res = await Api.manager.enrollment.update(enrollment.value.id, {
    end_date: null,
    enrolled_form_id: form?.id,
    provider_id: provider.value?.id,
    program_id: program.value?.id,
    start_date: startDate.value ? date.toISO(startDate.value) : null,
    rate: rate.value,
    period: period.value,
    status: 'enrolled',
    funding_source_ids: enrollment.value.funding_source_ids,
  });

  processing.value = false;

  if (res?.status !== 200) return;

  eventBus.chime('Saved');
  visible.value = false;
  emit('enrolled');
}

async function loadPrograms() {
  if (!provider.value) return;

  const { data } = await Api.program.index(provider.value.id, {
    subsidy_program_id: props.subsidyProgram?.id,
  });
  programs.value = data;
}

const minStartDate = computed(() => {
  return backdatingAllowed.value ? props.subsidyProgram?.enrollment_start_date : today.value;
});

async function setSelectedProgram() {
  await loadPrograms();

  if (!enrollment.value.program_id || !programs.value?.length) {
    program.value = null;
    return;
  }

  program.value = programs.value.find((p) => p.id === enrollment.value.program_id) || null;
}

async function open({ enrollmentValue }) {
  enrollment.value = enrollmentValue;
  startDate.value = setDefaultStartDate();
  rate.value = null;
  period.value = null;

  if (provider.value?.id !== enrollment.value.provider_id) {
    programs.value = [];
  }
  provider.value = props.providers.find((provider) => provider.id === enrollment.value.provider_id);

  await setSelectedProgram();

  visible.value = true;
  setFundingSources();
}

function setDefaultStartDate() {
  if (props.subsidyProgram?.enable_enrollment_start_date_backdating_by_providers) {
    let programStartDate = props.subsidyProgram?.enrollment_start_date;
    if (programStartDate) programStartDate = date.parseISO(programStartDate);
    return programStartDate > today.value ? programStartDate : today.value;
  }

  return null;
}

function setFundingSources() {
  if (props.subsidyProgram?.enable_enrollment_funding_sources) {
    Api.publicApi.organization.funding_source.index(
      {
        owner_id: props.subsidyProgram.id,
        enrollment_assignable: true,
      },
      (response) => {
        fundingSources.value = response.data;
      },
    );
  }
}

function submit() {
  if (props.subsidyProgram?.enrolled_form_schema_id) {
    enrolledFormDialog.value.open();
  } else {
    void enroll();
  }
}

watch(provider, (newVal) => newVal && void loadPrograms());

defineExpose({ open });
</script>
